<script lang="ts">
import { computed, defineComponent, inject, onActivated, onMounted, PropType, ref, watch } from "vue";
import Steps from "@/components/Steps.vue";
import Tarifauswahl from "@/components/tarif/Tarifauswahl.vue";
import TarifauswahlInformationenVHV from "@/components/tarif/TarifauswahlInformationenVHV";
import formatUtils from "@/businesslogic/utils/formatUtils";
import Leistungstabelle from "@/components/leistungstabelle/Leistungstabelle.vue";
import LeistungenVHVDirekt from "@/components/leistungstabelle/LeistungenVHV2024Direkt";
import LeistungenVHVMakler from "@/components/leistungstabelle/LeistungenVHV2024Makler";
import Tarifoptionen from "@/components/tarifoptionen/Tarifoptionen.vue";
import TarifoptionInformationenVHV from "@/components/tarifoptionen/TarifoptionInformationenVHV2024";
import Produktmodul from "@/components/module/Produktmodul.vue";
import ProduktmoduleVHV from "@/components/module/produktmoduleVHV2024";
import Beitrag from "@/components/Beitrag.vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import formValidierung from "@/businesslogic/formValidierung";
import EmailOderHerunterladen from "@/components/vorschlag/EmailOderHerunterladen.vue";
import VHVElementarZusatz from "@/components/module/VHVElementarZusatz.vue";
import Maklerrabatt from "@/components/maklerrabatt/Maklerrabatt.vue";
import VHVFahrradModul from "@/components/module/VHV2024FahrradModul.vue";
import { NavvItem } from "@/model/NavItem";
import IconSprite from "@/components/IconSprite.vue";
import Logger from "js-logger";
import HugRabatt from "@/components/HugRabatt.vue";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "VergleichVHV2024",
  components: {
    HugRabatt,
    VHVFahrradModul,
    VHVElementarZusatz,
    EmailOderHerunterladen,
    Steps,
    Tarifauswahl,
    Leistungstabelle,
    Tarifoptionen,
    Produktmodul,
    Beitrag,
    ValidierungsFehler,
    Maklerrabatt,
    IconSprite,
  },
  props: {
    navItems: Object as PropType<NavvItem[]>,
  },
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  setup(props, { emit }) {
    const piniaStore = useFormStore();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const tarifauswahlInformationen = ref(TarifauswahlInformationenVHV);
    const tarifoptionInformationen = ref(TarifoptionInformationenVHV);
    const produktmodule = ref(ProduktmoduleVHV);
    const { formatCurrency } = formatUtils();
    const aktiveLinie = ref(1);
    const vorschlagZugriff = ref("");

    const elementarZusatzRef = ref<InstanceType<typeof VHVElementarZusatz>>();

    const { validiereForm, getFormFehlerAnzahl, getFormFehler, validiereRabatt, validiereVorschaeden } =
      formValidierung();
    let validiert = false;

    const fehler = ref({} as any);

    const elementarHinweisText = computed(() => {
      if (piniaStore.state.isMakler && piniaStore.state.zuersZone.zone === "4") {
        return "Aufgrund Ihrer Eingabe kann für die Gefahr Rückstau und Überschwemmung im Rahmen der Elementarversicherung kein Versicherungsschutz angeboten werden.";
      }
      return undefined;
    });

    const leistungen = computed(() => {
      return piniaStore.state.isMakler ? LeistungenVHVMakler : LeistungenVHVDirekt;
    });

    const istGewaehlteProduktLinie = (produktLinie: string) => {
      return piniaStore.state.tarifinformationen.produktLinie === produktLinie;
    };

    const wechsleTarif = () => {
      if (piniaStore.state.tarifinformationen.produktLinie === "001") {
        aktiveLinie.value = 1;
      } else if (piniaStore.state.tarifinformationen.produktLinie === "002") {
        aktiveLinie.value = 2;
      } else if (piniaStore.state.tarifinformationen.produktLinie === "003") {
        aktiveLinie.value = 3;
      } else {
        Logger.debug("wechsleTarif(): Unbekannte Produktlinie:" + piniaStore.state.tarifinformationen.produktLinie);
      }
      validiereRabatt(piniaStore.state, fehler, validiert);
    };

    const eingabenSindValide = () => {
      return new Promise((resolve) => {
        validiereForm("phForm").then(() => {
          Logger.debug("Anzahl Fehler aus Validierung: " + getFormFehlerAnzahl());
          fehler.value = getFormFehler();

          if (piniaStore.state.tarifinformationen.sfr != 0) {
            validiereVorschaeden(piniaStore.state, fehler);
          }

          validiert = true;

          validiereRabatt(piniaStore.state, fehler, validiert);

          Logger.debug("Fehler-Objekt: ", fehler.value);
          resolve(getFormFehlerAnzahl() === 0);
        });
      });
    };

    const getTIB = (): string => {
      if (piniaStore.state.isMakler) {
        if (piniaStore.state.tarifinformationen.vemaKonzept) {
          return "https://www.gev-versicherung.de/downloads/pdf/TI-TIB-HR-VEMA-2024.pdf";
        } else {
          return "https://www.gev-versicherung.de/downloads/pdf/TI-TIB-HR-Makler-2024.pdf";
        }
      }
      return "https://www.gev-versicherung.de/downloads/pdf/TI-TIB-HR-Direkt-2024.pdf";
    };

    const modulAnzeige = (modulId: string, schalter: boolean): void => {
      const modul = produktmodule.value.find((i) => i.modulId === modulId);
      if (modul) {
        modul.anzeigen = schalter;
      }
    };

    onActivated(() => {
      modulAnzeigen(
        "vemaKonzept",
        piniaStore.state.isMakler && piniaStore.state.personendaten.vermittler.verbundzuordnung === "VEMA",
      );
      checkAktivierteModule(piniaStore.state.tarifinformationen.produktLinie);
    });

    const checkAktivierteModule = (tarif: string): void => {
      checkAktiviertesVemaModul(tarif);
    };

    const checkAktiviertesVemaModul = (produktLinie: string) => {
      console.log("Produktlinie für Module", produktLinie);
      const schalter =
        piniaStore.state.isMakler &&
        produktLinie === "003" &&
        piniaStore.state.personendaten.vermittler.verbundzuordnung === "VEMA";
      if (!schalter) {
        piniaStore.state.tarifinformationen.vemaKonzept = false;
      }

      modulAktivieren("vemaKonzept", schalter);
    };

    const modulAktivieren = (modulId: string, schalter: boolean): void => {
      const modul = produktmodule.value.find((i) => i.modulId === modulId);
      if (modul) modul.aktiv = schalter;
    };

    const modulAnzeigen = (modulId: string, schalter: boolean): void => {
      const modul = produktmodule.value.find((i) => i.modulId === modulId);
      if (modul) modul.anzeigen = schalter;
    };

    const checkAnzuzeigendeModule = (): void => {
      if (piniaStore.state.vhvElementarAusblenden) {
        modulAnzeige("elementar", false);
        piniaStore.state.tarifinformationen.elementar = false;
      } else {
        modulAnzeige("elementar", true);
      }
    };

    const aktualisiereAdresseWennElementar = () => {
      if (piniaStore.state.tarifinformationen.elementar) {
        piniaStore.state.adresseFelderSchrittDreiDeaktivieren = true;
        piniaStore.state.personendaten.versicherungsnehmer.adresse.ort = piniaStore.state.risikoAnschrift.ort;
        piniaStore.state.personendaten.versicherungsnehmer.adresse.strasse = piniaStore.state.risikoAnschrift.strasse;
        piniaStore.state.personendaten.versicherungsnehmer.adresse.hausnummer =
          piniaStore.state.risikoAnschrift.hausnummer;
      } else {
        piniaStore.state.adresseFelderSchrittDreiDeaktivieren = false;
      }
    };

    elementarZusatzRef.value?.validiereElementar(true);

    const zurNaechstenSeite = () => {
      aktualisiereAdresseWennElementar();
      emit("next-page", {
        pageIndex: 1,
      });
    };

    const validiereElementar = () => {
      elementarZusatzRef.value?.validiereElementar(true).then((isElementarValid) => {
        if (isElementarValid) {
          zurNaechstenSeite();
        }
      });
    };

    const nextPage = () => {
      eingabenSindValide().then((isValid) => {
        if (isValid) {
          if (piniaStore.state.tarifinformationen.elementar) {
            validiereElementar();
          } else {
            zurNaechstenSeite();
          }
        } else {
          document.querySelector("div#tariffOptionsTable")?.scrollIntoView({ behavior: "smooth" });
        }
      });
    };

    const validiereVorHerunterladenOderEmail = (art: string) => {
      if (piniaStore.state.tarifinformationen.elementar) {
        elementarZusatzRef.value?.validiereElementar(true).then((isElementarValid) => {
          if (isElementarValid) {
            vorschlagZugriff.value = art;
          } else {
            vorschlagZugriff.value = "";
          }
        });
      }
    };

    watch(
      () => piniaStore.state.tarifinformationen.abschlagZuschlagHoehe,
      () => {
        validiert = true;
        validiereRabatt(piniaStore.state, fehler, validiert);
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.vorschaedenAnzahl,
      (wertNeu) => {
        if (wertNeu != -1) {
          fehler.value["vorschaedenAnzahl"] = null;
        }
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.produktLinie,
      (tarifNeu, tarifAlt) => {
        if (tarifNeu !== tarifAlt) {
          wechsleTarif();
          checkAktivierteModule(tarifNeu);
          modulAnzeigen("vemaKonzept", piniaStore.state.isMakler);
        }
      },
    );

    watch(
      () => piniaStore.state.tarifinformationen.sfr,
      (wertNeu) => {
        if (wertNeu == 0) {
          piniaStore.state.tarifOptionenZuVerbergen.push("anzahlvorschaeden");
        } else {
          piniaStore.state.tarifOptionenZuVerbergen = [];
        }
      },
    );

    onMounted(() => {
      wechsleTarif();
      checkAnzuzeigendeModule();
    });

    onActivated(() => {
      checkAnzuzeigendeModule();
    });

    return {
      istGewaehlteProduktLinie,
      getTIB,
      nextPage,
      props,
      piniaStore,
      wechsleSichtbarkeit,
      istSichtbar,
      fehler,
      formatCurrency,
      tarifauswahlInformationen,
      leistungen,
      tarifoptionInformationen,
      produktmodule,
      aktiveLinie,
      elementarHinweisText,
      elementarZusatzRef,
      validiereVorHerunterladenOderEmail,
      vorschlagZugriff,
    };
  },
});
</script>

<template>
  <Steps v-bind:items="navItems" :active="1" @goto-page="$emit('goto-page', $event)" />
  <section>
    <tarifauswahl :tarifauswahlInformationen="tarifauswahlInformationen" />
    <leistungstabelle
      :inhalt="leistungen"
      :versicherungssumme="piniaStore.state.tarifinformationen.versicherungssumme"
      :aktiveLinie="aktiveLinie"
      :link="getTIB()"
    />
    <tarifoptionen :tarifoptionInformationen="tarifoptionInformationen" />
    <div v-if="piniaStore.state.isMakler && piniaStore.state.tarifinformationen.sfr === 0" class="o-grid">
      <div
        class="o-grid__cell o-grid__cell--12 o-grid__cell--9-480 o-grid__cell--8-768 o-grid__cell--7-1024 o-grid__cell--6-1200 o-grid__cell--offset-3 notice-box"
      >
        <strong>Hinweis:</strong> Das SFR-Modell ist obligatorisch, wenn mehr als 1 Vorschaden und eine Schadenquote >60
        % dem Antrag zu Grunde liegen
      </div>
    </div>
    <div v-if="fehler.vorschaedenAnzahl" class="o-grid">
      <div
        class="o-grid__cell o-grid__cell--12 o-grid__cell--9-480 o-grid__cell--8-768 o-grid__cell--7-1024 o-grid__cell--6-1200 o-grid__cell--offset-3-480"
      >
        <div class="o-grid o-grid--no-y-gutter o-grid--align-flex-end">
          <div class="o-grid__cell o-grid__cell--12">
            <validierungs-fehler :fehler-text="fehler.vorschaedenAnzahl" />
          </div>
        </div>
      </div>
    </div>
    <v-h-v-fahrrad-modul />
    <produktmodul :produktmodule="produktmodule" :zeige-icons="true">
      <template v-slot:vhvElementarHinweis>
        {{ elementarHinweisText }}
      </template>
      <template v-slot:vhvElementar>
        <v-h-v-elementar-zusatz :fehler="fehler" ref="elementarZusatzRef" />
      </template>
      <template v-slot:unbekannteZuersZoneHinweis>
        <div class="o-grid__cell o-grid__cell--9">
          <ul class="c-list">
            <li class="c-list__item c-list__item_warning">
              Der Beitrag kann ggf. steigen, da keine ZÜRS-Zone ermittelt werden konnte.
            </li>
          </ul>
        </div>
      </template>
    </produktmodul>
    <div class="o-grid o-grid--align-middle u-mar-t-2">
      <div class="o-grid__cell o-grid__cell--12 o-grid__cell--3-480 u-text-align-right--480 u-form-grid-nospace-y">
        <h2 class="u-form-heading unset-text-align">Rabatt</h2>
      </div>
      <hug-rabatt />
    </div>
    <div class="o-grid o-grid--align-middle u-mar-t-1">
      <div
        class="o-grid__cell o-grid__cell--12 o-grid__cell--3-480 u-text-align-right--480 u-form-grid-nospace-y"
      ></div>
      <div
        class="o-grid__cell o-grid__cell--12 o-grid__cell--9-480 o-grid__cell--8-768 o-grid__cell--7-1024 o-grid__cell--6-1200 u-form-grid-nospace-y"
      >
        <div class="c-form-control-checkbox gev19__contexthelp">
          <input
            v-model="piniaStore.state.tarifinformationen.buendelRabatt"
            id="buendelRabatt"
            name="buendelRabatt"
            class="c-form-control-checkbox__input"
            type="checkbox"
            :value="true"
            aria-label="Ja, es gibt bereits eine GEV Wohngebäude-Versicherung für dieselbe Anschrift"
            data-cy="buendelRabatt"
            tabindex="0"
          />
          <label class="c-form-control-checkbox__label" for="buendelRabatt" style="max-width: 92%">
            <icon-sprite icon-name="icon-check-mark" css-klassen="c-form-control-checkbox__icon" :ist-klein="true" />
            <span class="c-form-control-checkbox__label-text">
              {{
                piniaStore.state.isMakler === true
                  ? "Der Versicherungsnehmer besitzt bereits eine GEV Wohngebäude-Versicherung für dieselbe Anschrift "
                  : "Ja, ich habe bereits eine GEV Wohngebäude-Versicherung für dieselbe Anschrift "
              }}<strong>(25% Rabatt)</strong>
            </span>
          </label>
        </div>
      </div>
    </div>
    <maklerrabatt :fehler="fehler" />
    <beitrag @next-page="nextPage"></beitrag>
    <email-oder-herunterladen
      produkt="vhv2024"
      tracking-zusatz="vergleich"
      @validierung-erforderlich="validiereVorHerunterladenOderEmail"
      :vorschlagZugriff="vorschlagZugriff"
    />
  </section>
</template>
